import { useCallback, useEffect, useRef, useState } from 'react';
import {
  countStockByBranch,
  fetchProductsWithoutSales,
  STOCK_NO_SALES_DAYS_DEFAULT,
  type ProductsWithoutSalesResult,
} from '../stock/public-api';

export type InventoryNoSalesAlert = {
  days: number;
  count: number;
  productIds: string[];
  loading: boolean;
  refresh: () => Promise<void>;
};

/**
 * Alerta Stock: productos con qty>0 sin venta en N días (default 30).
 * Independiente del listado filtrado; se refresca con KPIs / mutaciones.
 *
 * `count` se recalcula con `countStockByBranch` (mismo pipeline que el
 * filtro `no_sales`): el RPC cuenta productos que el listado no muestra
 * (stock vencido, filas fuera del snapshot) y el strip mentía el número.
 */
export function useInventoryNoSalesAlert(
  currentBranchId: string | null | undefined,
  days: number = STOCK_NO_SALES_DAYS_DEFAULT,
): InventoryNoSalesAlert {
  const [result, setResult] = useState<ProductsWithoutSalesResult | null>(null);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const branchRef = useRef(currentBranchId);
  const daysRef = useRef(days);

  const refresh = useCallback(async () => {
    const branchId = branchRef.current;
    if (!branchId) {
      setResult(null);
      setLoading(false);
      return;
    }
    const gen = ++generationRef.current;
    setLoading(true);
    try {
      const data = await fetchProductsWithoutSales(branchId, daysRef.current);
      let count = data?.count ?? 0;
      try {
        count = await countStockByBranch(branchId, {
          statusFilter: 'no_sales',
          productIdAllowlist: data?.productIds ?? [],
        });
      } catch {
        // Fallback: count del RPC.
      }
      if (gen !== generationRef.current || !mountedRef.current) return;
      setResult(data ? { ...data, count } : null);
    } catch (error) {
      console.warn('[InventoryNoSales] No se pudo cargar la alerta', error);
      if (gen === generationRef.current && mountedRef.current) {
        setResult(null);
      }
    } finally {
      if (gen === generationRef.current && mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    branchRef.current = currentBranchId;
    daysRef.current = days;
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [currentBranchId, days, refresh]);

  return {
    days: result?.days ?? days,
    count: result?.count ?? 0,
    productIds: result?.productIds ?? [],
    loading,
    refresh,
  };
}
